/**
 * Générateur pseudo-aléatoire déterministe (sfc32) et fonctions de hachage.
 *
 * Le même code tourne côté serveur (simulation autoritative) et côté client
 * (décor procédural : sapins, rochers). L'état tient en 4 entiers 32 bits :
 * il se sérialise dans la sauvegarde pour reprendre exactement la même suite
 * de tirages après un redémarrage du serveur.
 */

export type RngState = [number, number, number, number];

/** splitmix32 : étale une graine entière sur 32 bits. */
function splitmix32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x9e3779b9) >>> 0;
    let z = s;
    z = Math.imul(z ^ (z >>> 16), 0x85ebca6b) >>> 0;
    z = Math.imul(z ^ (z >>> 13), 0xc2b2ae35) >>> 0;
    return (z ^ (z >>> 16)) >>> 0;
  };
}

export class Rng {
  private a: number;
  private b: number;
  private c: number;
  private d: number;

  constructor(seed: number | RngState) {
    if (Array.isArray(seed)) {
      [this.a, this.b, this.c, this.d] = seed.map((v) => v >>> 0) as RngState;
    } else {
      const sm = splitmix32(seed);
      this.a = sm();
      this.b = sm();
      this.c = sm();
      this.d = sm();
      for (let i = 0; i < 12; i++) this.next();
    }
  }

  state(): RngState {
    return [this.a >>> 0, this.b >>> 0, this.c >>> 0, this.d >>> 0];
  }

  /** Flottant uniforme dans [0, 1). */
  next(): number {
    const t0 = (this.a + this.b) | 0;
    this.a = this.b ^ (this.b >>> 9);
    this.b = (this.c + (this.c << 3)) | 0;
    this.c = (this.c << 21) | (this.c >>> 11);
    this.d = (this.d + 1) | 0;
    const t = (t0 + this.d) | 0;
    this.c = (this.c + t) | 0;
    return (t >>> 0) / 4294967296;
  }

  range(min: number, max: number): number {
    return min + (max - min) * this.next();
  }

  /** Entier dans [min, max] inclus. */
  int(min: number, max: number): number {
    return min + Math.floor(this.next() * (max - min + 1));
  }

  chance(p: number): boolean {
    return this.next() < p;
  }

  pick<T>(items: readonly T[]): T {
    return items[Math.floor(this.next() * items.length)] as T;
  }

  /** Tirage pondéré : renvoie l'index choisi. */
  weighted(weights: readonly number[]): number {
    let total = 0;
    for (const w of weights) total += Math.max(0, w);
    if (total <= 0) return Math.floor(this.next() * weights.length);
    let r = this.next() * total;
    for (let i = 0; i < weights.length; i++) {
      r -= Math.max(0, weights[i] as number);
      if (r < 0) return i;
    }
    return weights.length - 1;
  }

  /** Loi normale approchée (somme de 4 uniformes), sans fonction transcendante. */
  normal(mean = 0, sd = 1): number {
    const s = this.next() + this.next() + this.next() + this.next() - 2;
    return mean + sd * s * 1.7320508;
  }
}

/** Hachage entier 32 bits de 1 à 3 entiers (déterministe, sans état). */
export function hash32(x: number, y = 0, seed = 0): number {
  let h = (seed ^ 0x27d4eb2d) >>> 0;
  h = Math.imul(h ^ (x | 0), 0x9e3779b1) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca77) >>> 0;
  h = Math.imul(h ^ (y | 0), 0xc2b2ae3d) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0x27d4eb2f) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

/** Hachage vers un flottant dans [0, 1). */
export function hashFloat(x: number, y = 0, seed = 0): number {
  return hash32(x, y, seed) / 4294967296;
}
